// utils/useDefaultSuspenseInfiniteQuery.ts
import {
  InfiniteData,
  QueryKey,
  UseSuspenseInfiniteQueryOptions,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query"
import { useHandleAsyncSafely } from "../typed-client/hooks/use-handle-async-safely"
import { getDefaultQueryKey } from "./getDefaultQueryKey"
import { Pagination } from "./Pagination"
import { PageRequest } from "../@client/Theasoft_ApiServer__Utils_Pagination"

interface IUseDefaultSuspenseInfiniteQueryOptions<
  // we want to allow any type of query result,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends Pagination<any>,
  TError = Error,
> extends Partial<
    UseSuspenseInfiniteQueryOptions<
      TData,
      TError,
      InfiniteData<TData>,
      TData,
      QueryKey,
      PageRequest
    >
  > {
  pageSize?: number
}

export function useDefaultSuspenseInfiniteQuery<
  // args and and return of the queryFn can be anything
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TQueryFn extends (...args: any[]) => Promise<TData>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends Pagination<any> = Awaited<ReturnType<TQueryFn>>,
>(
  queryFn: TQueryFn,
  paramsOrOptions?:
    | ((pageRequest: PageRequest) => Parameters<TQueryFn>[0])
    | IUseDefaultSuspenseInfiniteQueryOptions<TData>,
  maybeOptions?: IUseDefaultSuspenseInfiniteQueryOptions<TData>
) {
  const handleAsyncSafely = useHandleAsyncSafely<TData>()

  const isGetParams = typeof paramsOrOptions === "function"

  const getParams: Parameters<TQueryFn>[0] = isGetParams
    ? paramsOrOptions
    : () => {}
  const options: IUseDefaultSuspenseInfiniteQueryOptions<TData> | undefined =
    isGetParams ? (maybeOptions ?? undefined) : (paramsOrOptions ?? undefined)

  return useSuspenseInfiniteQuery<
    TData,
    Error,
    InfiniteData<TData>,
    QueryKey,
    PageRequest
  >({
    queryKey: getDefaultQueryKey(queryFn, getParams()),
    queryFn: ({ pageParam: pageRequest }) => {
      return handleAsyncSafely(() => queryFn(getParams(pageRequest)))
    },
    initialPageParam: { pageParam: 0, pageSize: options?.pageSize ?? 20 },
    getNextPageParam: (response) =>
      response.hasNextPage && response.pageNumber !== undefined
        ? {
            pageParam: response.pageNumber + 1,
            pageSize: options?.pageSize ?? 20,
          }
        : undefined,
    ...options,
  })
}
